import {
  PropsWithChildren,
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import type { AppMode, SessionResponseDto } from "@madamuh/domain";
import { ApiError } from "../api/client";
import {
  fetchCurrentSession,
  patchMemberMode,
  postLineLogin,
  postMemberInviteConsume,
  postOwnerOnboardingConsume,
  postStudentSelfInviteConsume
} from "../api/session";

type SessionBootstrapStatus = "idle" | "loading" | "authenticated" | "unauthenticated" | "error";

type SessionBootstrapContextValue = {
  status: SessionBootstrapStatus;
  session: SessionResponseDto | null;
  errorMessage: string | null;
  bootstrapSession: (tenantSlug: string) => Promise<SessionResponseDto | null>;
  verifySession: (tenantSlug: string) => Promise<
    | { status: "authenticated"; session: SessionResponseDto }
    | { status: "unauthenticated" }
    | { status: "error"; errorMessage: string }
  >;
  switchAppMode: (tenantSlug: string, appMode: AppMode) => Promise<SessionResponseDto>;
  establishSessionWithLineToken: (tenantSlug: string, idToken: string) => Promise<SessionResponseDto>;
  consumeOwnerOnboardingToken: (tenantSlug: string, token: string) => Promise<SessionResponseDto>;
  consumeMemberInviteToken: (tenantSlug: string, token: string) => Promise<SessionResponseDto>;
  consumeStudentSelfInviteToken: (tenantSlug: string, token: string) => Promise<SessionResponseDto>;
  lineLoginWithMockToken: (tenantSlug: string, idToken: string) => Promise<SessionResponseDto | null>;
  resetBootstrapState: () => void;
};

const SessionBootstrapContext = createContext<SessionBootstrapContextValue | null>(null);
const DEFAULT_VERIFY_DELAYS_MS = [0, 150, 350];
const POST_LOGIN_VERIFY_DELAYS_MS = [0, 150, 350, 750, 1500, 2500];

function isSessionForTenant(session: SessionResponseDto, tenantSlug: string): boolean {
  return session.tenant.tenantSlug === tenantSlug;
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

class SessionCookieEstablishmentError extends Error {
  constructor() {
    super("Session cookie could not be established");
    this.name = "SessionCookieEstablishmentError";
  }
}

export function SessionBootstrapProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<SessionBootstrapStatus>("idle");
  const [session, setSession] = useState<SessionResponseDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sessionRef = useRef<SessionResponseDto | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const verifySessionWithDelays = useCallback(async (
    tenantSlug: string,
    delayScheduleMs: number[]
  ): Promise<
    | { status: "authenticated"; session: SessionResponseDto }
    | { status: "unauthenticated" }
    | { status: "error"; errorMessage: string }
  > => {
    let lastUnauthorizedError: ApiError | null = null;

    for (const delayMs of delayScheduleMs) {
      if (delayMs > 0) {
        await sleep(delayMs);
      }

      try {
        const nextSession = await fetchCurrentSession();

        if (!isSessionForTenant(nextSession, tenantSlug)) {
          setSession(null);
          setStatus("unauthenticated");
          return { status: "unauthenticated" };
        }

        setSession(nextSession);
        setStatus("authenticated");
        return {
          status: "authenticated",
          session: nextSession
        };
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          lastUnauthorizedError = error;
          continue;
        }

        const nextErrorMessage = error instanceof Error ? error.message : "Session bootstrap failed";
        setSession(null);
        setStatus("error");
        setErrorMessage(nextErrorMessage);
        return {
          status: "error",
          errorMessage: nextErrorMessage
        };
      }
    }

    if (lastUnauthorizedError) {
      setSession(null);
      setStatus("unauthenticated");
      return { status: "unauthenticated" };
    }

    const nextErrorMessage = "Session bootstrap failed";
    setSession(null);
    setStatus("error");
    setErrorMessage(nextErrorMessage);
    return {
      status: "error",
      errorMessage: nextErrorMessage
    };
  }, []);

  const verifySession = useCallback(async (
    tenantSlug: string
  ): Promise<
    | { status: "authenticated"; session: SessionResponseDto }
    | { status: "unauthenticated" }
    | { status: "error"; errorMessage: string }
  > => {
    setStatus("loading");
    setErrorMessage(null);
    return verifySessionWithDelays(tenantSlug, DEFAULT_VERIFY_DELAYS_MS);
  }, [verifySessionWithDelays]);

  const bootstrapSession = useCallback(async (tenantSlug: string): Promise<SessionResponseDto | null> => {
    const result = await verifySession(tenantSlug);
    return result.status === "authenticated" ? result.session : null;
  }, [verifySession]);

  const resetBootstrapState = useCallback(() => {
    setSession(null);
    setStatus("idle");
    setErrorMessage(null);
  }, []);

  const establishSessionWithLineToken = useCallback(async (tenantSlug: string, idToken: string): Promise<SessionResponseDto> => {
    setStatus("loading");
    setErrorMessage(null);

    try {
      await postLineLogin({ tenantSlug, idToken });
      const verifiedSession = await verifySessionWithDelays(tenantSlug, POST_LOGIN_VERIFY_DELAYS_MS);

      if (verifiedSession.status === "authenticated") {
        return verifiedSession.session;
      }

      if (verifiedSession.status === "error") {
        throw new Error(verifiedSession.errorMessage);
      }

      throw new SessionCookieEstablishmentError();
    } catch (error) {
      setSession(null);

      if (error instanceof SessionCookieEstablishmentError) {
        setStatus("unauthenticated");
        setErrorMessage(error.message);
        throw error;
      }

      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "LINE login failed");
      throw error instanceof Error ? error : new Error("LINE login failed");
    }
  }, [verifySessionWithDelays]);

  const switchAppMode = useCallback(async (tenantSlug: string, appMode: AppMode): Promise<SessionResponseDto> => {
    setStatus("loading");
    setErrorMessage(null);

    try {
      const nextSession = await patchMemberMode(tenantSlug, appMode);
      setSession(nextSession);
      setStatus("authenticated");
      return nextSession;
    } catch (error) {
      setStatus(sessionRef.current ? "authenticated" : "error");
      setErrorMessage(error instanceof Error ? error.message : "App mode switch failed");
      throw error instanceof Error ? error : new Error("App mode switch failed");
    }
  }, []);

  const consumeOwnerOnboardingToken = useCallback(async (
    tenantSlug: string,
    token: string
  ): Promise<SessionResponseDto> => {
    setStatus("loading");
    setErrorMessage(null);

    try {
      const consumedSession = await postOwnerOnboardingConsume(tenantSlug, token);
      const verifiedSession = await verifySessionWithDelays(tenantSlug, [0, 50, 150]);

      if (verifiedSession.status === "authenticated") {
        return verifiedSession.session;
      }

      setSession(consumedSession);
      setStatus("authenticated");
      return consumedSession;
    } catch (error) {
      setStatus(sessionRef.current ? "authenticated" : "error");
      setErrorMessage(error instanceof Error ? error.message : "Owner onboarding consume failed");
      throw error instanceof Error ? error : new Error("Owner onboarding consume failed");
    }
  }, [verifySessionWithDelays]);

  const consumeMemberInviteToken = useCallback(async (
    tenantSlug: string,
    token: string
  ): Promise<SessionResponseDto> => {
    setStatus("loading");
    setErrorMessage(null);

    try {
      const consumedSession = await postMemberInviteConsume(tenantSlug, token);
      const verifiedSession = await verifySessionWithDelays(tenantSlug, [0, 50, 150]);

      if (verifiedSession.status === "authenticated") {
        return verifiedSession.session;
      }

      setSession(consumedSession);
      setStatus("authenticated");
      return consumedSession;
    } catch (error) {
      setStatus(sessionRef.current ? "authenticated" : "error");
      setErrorMessage(error instanceof Error ? error.message : "Member invite consume failed");
      throw error instanceof Error ? error : new Error("Member invite consume failed");
    }
  }, [verifySessionWithDelays]);

  const consumeStudentSelfInviteToken = useCallback(async (
    tenantSlug: string,
    token: string
  ): Promise<SessionResponseDto> => {
    setStatus("loading");
    setErrorMessage(null);

    try {
      const consumedSession = await postStudentSelfInviteConsume(tenantSlug, token);
      setSession(consumedSession);
      setStatus("authenticated");
      return consumedSession;
    } catch (error) {
      setStatus(sessionRef.current ? "authenticated" : "error");
      setErrorMessage(error instanceof Error ? error.message : "Student self invite consume failed");
      throw error instanceof Error ? error : new Error("Student self invite consume failed");
    }
  }, []);

  const lineLoginWithMockToken = useCallback(async (tenantSlug: string, idToken: string): Promise<SessionResponseDto | null> => {
    try {
      return await establishSessionWithLineToken(tenantSlug, idToken);
    } catch {
      return null;
    }
  }, [establishSessionWithLineToken]);

  return (
    <SessionBootstrapContext.Provider
      value={{
        status,
        session,
        errorMessage,
        bootstrapSession,
        verifySession,
        switchAppMode,
        establishSessionWithLineToken,
        consumeOwnerOnboardingToken,
        consumeMemberInviteToken,
        consumeStudentSelfInviteToken,
        lineLoginWithMockToken,
        resetBootstrapState
      }}
    >
      {children}
    </SessionBootstrapContext.Provider>
  );
}

export function useSessionBootstrap() {
  const context = useContext(SessionBootstrapContext);

  if (!context) {
    throw new Error("useSessionBootstrap must be used within SessionBootstrapProvider");
  }

  return context;
}
